/**
 * Live progress for tearsheet generation.
 *
 * The AI worker writes `report_status.tearsheet = "generating_<stage>"` as it
 * moves through generation, and the status socket delivers each change. This
 * turns those values into what the user sees.
 */

export interface TearsheetStage {
  status: string;
  label: string;
  /** Rough share of the run done once this stage starts, for the progress bar. */
  percent: number;
}

export const TEARSHEET_STAGES: TearsheetStage[] = [
  { status: "generating_analyzing_sections", label: "Analyzing the deal, section by section…", percent: 10 },
  { status: "generating_summarizing", label: "Summarizing what stands out…", percent: 25 },
  { status: "generating_gathering_context", label: "Gathering the deal's data…", percent: 35 },
  { status: "generating_writing", label: "Writing the tearsheet…", percent: 55 },
  { status: "generating_researching", label: "Looking up company details…", percent: 55 },
  { status: "generating_finalizing", label: "Finalizing…", percent: 90 },
];

const QUEUED_STATUSES = new Set(["queued", "pending"]);
const STARTED_STATUSES = new Set(["in_progress", "inprogress"]);

/** True while a tearsheet is queued or generating, whatever stage it's in. */
export function isTearsheetGenerating(status: string | undefined | null): boolean {
  if (!status) return false;
  return (
    QUEUED_STATUSES.has(status) ||
    STARTED_STATUSES.has(status) ||
    status.startsWith("generating_")
  );
}

/** What to show for a status, or null when nothing is generating. */
export function tearsheetProgress(
  status: string | undefined | null
): { label: string; percent: number } | null {
  if (!isTearsheetGenerating(status)) return null;

  const stage = TEARSHEET_STAGES.find((s) => s.status === status);
  if (stage) return { label: stage.label, percent: stage.percent };
  if (status && QUEUED_STATUSES.has(status)) return { label: "Queued…", percent: 2 };
  // `in_progress`, or a stage this build doesn't know yet — still progress.
  return { label: "Starting…", percent: 5 };
}
